/**
 * tessellationMeltPipeline — a jittered square grid in mid-relaxation.
 *
 * A square grid of nodes where each node has been perturbed by FBM
 * noise. The cells are drawn as a Voronoi-like tessellation, but
 * with each cell's color and edge fading based on how far the local
 * area is from perfect relaxation. The "melt" comes from:
 *   - Cell centers slowly drift on a slow noise field.
 *   - Cells whose distance to neighbors is too close collapse
 *     (their edges brighten and the cell shrinks).
 *   - Cells whose distance is too far stretch (their edges darken
 *     and they expand).
 * The net effect is a continuously-evolving tessellation that looks
 * like metal cooling, or soap-film adjusting.
 *
 * Innovations:
 *   - Per-cell drift field that uses FBM with different phase per
 *     cell, so the whole grid never moves coherently.
 *   - Voronoi distance comparison via the partial evaluator —
 *     just check the distance to the 2 nearest sites, the ratio
 *     tells us how "stressed" the local tessellation is.
 *   - Edge anti-aliasing via dFdx/dFdy on the Voronoi distance.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface TessellationMeltPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// 2D value noise.
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return v;
}

// Returns: (F1, F2, cellId.x, cellId.y)
// F1 = distance to nearest site, F2 = distance to second nearest.
// The difference sqrt(F2) - sqrt(F1) approximates the edge distance.
vec4 voronoi(vec2 p) {
  vec2 cell = floor(p);
  vec2 local = fract(p);
  float F1 = 1e9;
  float F2 = 1e9;
  vec2 id1 = vec2(0.0);
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 offset = vec2(float(i), float(j));
      vec2 site = offset + vec2(
        hash21(cell + offset + vec2(1.0, 0.0)),
        hash21(cell + offset + vec2(0.0, 1.0))
      );
      float d = length(local - site);
      if (d < F1) {
        F2 = F1;
        F1 = d;
        id1 = cell + offset;
      } else if (d < F2) {
        F2 = d;
      }
    }
  }
  return vec4(F1, F2, id1);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Drift: each cell's domain shifts based on a slow FBM.
  // The drift is large enough that the cells visibly move.
  vec2 drift = vec2(
    fbm(p * 0.5 + vec2(uTime * 0.1, 0.0)),
    fbm(p * 0.5 + vec2(0.0, uTime * 0.13))
  ) * 0.4;

  // Cell spacing — increases with bass so the tessellation expands.
  float spacing = 3.0 + bass * 1.0;
  vec4 v = voronoi((p + drift) * spacing);
  float F1 = v.x;
  float F2 = v.y;
  vec2 cellId = v.zw;

  // Edge distance.
  float edge = sqrt(F2) - sqrt(F1);

  // Per-cell hue — driven by the cell's coordinates.
  float cellHue = hash21(cellId);
  float cellBright = fbm(cellId * 0.5 + vec2(uTime * 0.05, 0.0));

  // Background — dark with a hint of ambient color.
  float r = length(p);
  vec3 bg = mix(
    vec3(0.06, 0.08, 0.14),
    vec3(0.01, 0.01, 0.04),
    smoothstep(0.0, 0.7, r)
  );

  // Cell color — three colors based on cellBright (which acts as a
  // "stress" indicator).
  vec3 colCool = vec3(0.05, 0.45, 0.85);
  vec3 colMid = vec3(0.85, 0.55, 0.20);
  vec3 colWarm = vec3(0.95, 0.20, 0.45);
  float t = cellBright + 0.3 * sin(uTime * 0.15 + cellHue * 6.28);
  vec3 cellColor = mix(colCool, colMid, smoothstep(0.3, 0.5, t));
  cellColor = mix(cellColor, colWarm, smoothstep(0.55, 0.75, t));

  // Cell body — fades with F1 (closer to center = brighter).
  float body = 1.0 - smoothstep(0.0, 0.5, F1);
  vec3 col = mix(bg, cellColor, body * 0.6);

  // Cell edges — bright ridges where edge is small.
  float ridge = 1.0 - smoothstep(0.0, 0.04, edge);
  // AA via dFdx/dFdy.
  float edgeWidth = fwidth(edge) * 0.7;
  float ridgeAA = 1.0 - smoothstep(0.0, edgeWidth + 0.005, edge);
  col += vec3(1.0, 0.95, 0.85) * ridgeAA * (0.5 + 0.5 * mid);

  // Stress cells — where neighbors are very close, brighten.
  float stress = smoothstep(0.10, 0.0, edge);
  col += vec3(1.0, 0.6, 0.3) * stress * 0.4 * high;

  // A few "highlight" cells whose brightness pulses with bass.
  float pulseCell = step(0.85, cellHue);
  float pulse = 0.5 + 0.5 * sin(uTime * 4.0 + cellHue * 30.0);
  col += vec3(1.0, 0.85, 0.5) * pulseCell * pulse * body * bass * 0.8;

  // Slow drift arrows — short lines from cell center to drift direction.
  // Drawn by checking if the current pixel is near the cell's center
  // and within a small offset along the drift direction.
  vec2 centerLocal = fract((p + drift) * spacing);
  vec2 cellCenter = vec2(0.5);
  float centerD = length(centerLocal - cellCenter);
  float driftArrow = exp(-centerD * 25.0) * 0.3;
  col += vec3(0.5, 0.8, 1.0) * driftArrow * 0.3;

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Vignette.
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computeTessellationMeltBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeTessellationMeltRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createTessellationMeltPipeline(gl: WebGL2RenderingContext): TessellationMeltPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawTessellationMelt(
  gl: WebGL2RenderingContext,
  pipe: TessellationMeltPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
